// src/pages/admin/hr/Attendance.jsx
import { useState, useEffect, useCallback } from 'react'
import { 
  Calendar, 
  Clock, 
  Users, 
  RefreshCw, 
  Plus, 
  ChevronDown,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  UserX
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import toast from 'react-hot-toast'
import AttendanceService from '../../../services/attendanceService'
import EmployeeService from '../../../services/employeeService'
import BranchService from '../../../services/branchService'
import CreateAttendanceModal from '../../../components/modals/CreateAttendanceModal'
import EditAttendanceModal from '../../../components/modals/EditAttendanceModal'
import ViewAttendanceModal from '../../../components/modals/ViewAttendanceModal'
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal'
import { cn } from '../../../utils/cn'

const Attendance = () => {
  // State management
  const [attendances, setAttendances] = useState([])
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedAttendance, setSelectedAttendance] = useState(null)
  
  // Filter states
  const [filters, setFilters] = useState({
    employee: '',
    branch: '',
    status: '',
    date: ''
  })
  const [searchTerm, setSearchTerm] = useState('')

  // Define fetchAttendances function first
  const fetchAttendances = useCallback(async () => {
    try {
      setLoading(true)
      
      // Build query parameters
      const params = {}
      if (filters.employee) params.employee = filters.employee
      if (filters.branch) params.branch = filters.branch
      if (filters.status) params.status = filters.status
      if (filters.date) params.date = filters.date
      if (searchTerm) params.search = searchTerm

      const response = await AttendanceService.getAllAttendance(params)
      
      if (response.success) {
        setAttendances(response.data.attendances || [])
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      console.error('Error fetching attendances:', error)
      toast.error('Failed to fetch attendance records')
    } finally {
      setLoading(false)
    }
  }, [filters, searchTerm])

  // Fetch initial data
  useEffect(() => {
    fetchAttendances()
    fetchEmployees()
    fetchBranches()
  }, [fetchAttendances])

  // Apply filters when they change
  useEffect(() => {
    fetchAttendances()
  }, [fetchAttendances])

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAllEmployees()
      if (response.success) {
        setEmployees(response.data.employees || response.data || [])
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
    }
  }

  const fetchBranches = async () => {
    try {
      const response = await BranchService.getAllBranches()
      if (response.success) {
        setBranches(response.data.branches || response.data || [])
      }
    } catch (error) {
      console.error('Error fetching branches:', error)
    }
  }

  const handleCreateAttendance = async (attendanceData) => {
    try {
      setActionLoading(true)
      await AttendanceService.createAttendance(attendanceData)
      
      // If we get here, it was successful
      toast.success('Attendance record created successfully')
      setShowCreateModal(false)
      fetchAttendances()
    } catch (error) {
      console.error('Error creating attendance:', error)
      
      // Re-throw the original error so modal can handle it properly
      throw error
    } finally {
      setActionLoading(false)
    }
  }

  const handleUpdateAttendance = async (attendanceId, updateData) => {
    try {
      setActionLoading(true)
      const response = await AttendanceService.updateAttendance(attendanceId, updateData)
      
      if (response.success) {
        toast.success('Attendance record updated successfully')
        setShowEditModal(false)
        setSelectedAttendance(null)
        fetchAttendances()
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      console.error('Error updating attendance:', error)
      toast.error('Failed to update attendance record')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteAttendance = async (attendanceId) => {
    const idToDelete = attendanceId || selectedAttendance?._id
    if (!idToDelete) return

    try {
      setActionLoading(true)
      const response = await AttendanceService.deleteAttendance(idToDelete)
      
      if (response.success) {
        toast.success('Attendance record deleted successfully')
        setShowDeleteModal(false)
        setSelectedAttendance(null)
        fetchAttendances()
      } else {
        toast.error(response.message || 'Failed to delete attendance record')
      }
    } catch (error) {
      console.error('Error deleting attendance:', error)
      toast.error('Failed to delete attendance record')
    } finally {
      setActionLoading(false)
    }
  }

  const handleResetFilters = () => {
    setFilters({
      employee: '',
      branch: '',
      status: '',
      date: ''
    })
    setSearchTerm('')
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Present' },
      absent: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Absent' },
      on_leave: { color: 'bg-blue-100 text-blue-800', icon: UserX, label: 'On Leave' }
    }

    const config = statusConfig[status] || statusConfig.present
    const StatusIcon = config.icon

    return (
      <span className={cn("inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium", config.color)}>
        <StatusIcon className="h-3 w-3" />
        <span>{config.label}</span>
      </span>
    )
  }

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy')
    } catch {
      return dateString
    }
  }

  const formatTime = (timeString) => {
    if (!timeString) return '-'
    return timeString
  }

  const calculateDuration = (attendance) => {
    if (attendance.workingHours !== undefined && attendance.workingHours !== null) {
      const hours = parseFloat(attendance.workingHours)
      if (isNaN(hours)) {
        return '0.00 hrs'
      }
      return `${hours.toFixed(2)} hrs`
    }
    return '-'
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Employee Attendance</h1>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchAttendances}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            <Plus className="h-4 w-4" />
            <span>Mark Attendance</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-medium text-gray-900">Filter Employees</h2>
          </div>
          {(searchTerm || filters.branch || filters.status || filters.date) && (
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Search/Employee Filter */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Branch Filter */}
          <div className="relative">
            <select
              value={filters.branch}
              onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
            >
              <option value="">All Types</option>
              {branches.map(branch => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
            >
              <option value="">All Status</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="on_leave">On Leave</option>
            </select>
            <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Date Filter */}
          <div>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div className="text-sm text-gray-600">
          Showing {attendances.length} of {attendances.length} employees
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Attendance</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock-in</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock out</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Loading attendance records...</span>
                    </div>
                  </td>
                </tr>
              ) : attendances.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No attendance records found</p>
                  </td>
                </tr>
              ) : (
                attendances.map((attendance) => (
                  <tr key={attendance._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(attendance.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {attendance.employee?.fullName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {attendance.shift?.shiftName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {attendance.branch?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {calculateDuration(attendance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTime(attendance.clockIn)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTime(attendance.clockOut)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(attendance.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedAttendance(attendance)
                            setShowViewModal(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAttendance(attendance)
                            setShowEditModal(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAttendance(attendance)
                            setShowDeleteModal(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <CreateAttendanceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateAttendance}
        loading={actionLoading}
        employees={employees}
        existingAttendances={attendances}
      />

      {selectedAttendance && (
        <>
          <EditAttendanceModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false)
              setSelectedAttendance(null)
            }}
            onSubmit={handleUpdateAttendance}
            loading={actionLoading}
            attendance={selectedAttendance}
          />

          <ViewAttendanceModal
            isOpen={showViewModal}
            onClose={() => {
              setShowViewModal(false)
              setSelectedAttendance(null)
            }}
            attendance={selectedAttendance}
          />

          <ConfirmDeleteModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false)
              setSelectedAttendance(null)
            }}
            onConfirm={handleDeleteAttendance}
            loading={actionLoading}
            attendance={selectedAttendance}
          />
        </>
      )}
    </div>
  )
}

export default Attendance
